import type { paramsTypeAllRequired } from '~~/shared/api/rurality/v3/search/cottages/paramsSchema'
import type { schemaType as CottageListData } from '~~/shared/api/rurality/v3/search/cottages/schema'
import { useFacetParams } from '~~/modules/views/cottage-list/composables/useFacetParams'
import { useHometogo } from '~~/modules/integrations/hometogo/composables/useHometogo'
import routesValidation from '~/utils/routesValidation'

export interface CottageListFilters {
  f?: string
  l?: string
  minPrice?: number
  maxPrice?: number
  dateIn?: string
  dateOut?: string
  c?: number
  a?: string
  cor?: string
  ec?: number
  r?: string
  bo?: number
  of?: number
  sort?: string
  q?: string
  ne?: string
  sw?: string
  fc?: number
  compareWithHomeToGo?: boolean
}

export function useCottageListFilters() {
  const route = useRoute()
  const { getFacetParams } = useFacetParams()
  const { shouldActivatePopunder, navigateWithPopunder } = useHometogo()
  const { isCottageDetailRoute } = routesValidation()

  /**
   * Parse URL query parameters into filter object
   */
  function parseUrlParams(): paramsTypeAllRequired {
    const query = route.query
    return {
      page: query.page ? Number(query.page) : 1,
      l: (query.l as string) || undefined,
      f: (query.f as string) || undefined,
      min_price: query.min_price ? Number(query.min_price) * 100 : undefined,
      max_price: query.max_price ? Number(query.max_price) * 100 : undefined,
      d: (query.d as string) || undefined,
      c: query.c ? Number(query.c) : undefined,
      a: (query.a as string) || undefined,
      cor: (query.cor as string) || undefined,
      ec: query.ec ? (1 as const) : undefined,
      r: (query.r as string) || undefined,
      bo: query.bo ? (1 as const) : undefined,
      of: query.of ? (1 as const) : undefined,
      sort: (query.sort as string) || undefined,
      q: (query.q as string) || undefined,
      ne: (query.ne as string) || undefined,
      sw: (query.sw as string) || undefined,
      fc: query.fc ? (1 as const) : undefined,
    }
  }

  /**
   * Build URL query object from filters.
   * @param targetPath - the path we are navigating to; used to decide whether
   *   to include the location param (omitted when location lives in the path slug).
   */
  function buildUrlQuery(
    filters: CottageListFilters,
    page?: number,
    currentPage?: number,
    targetPath?: string,
  ): Record<string, string> {
    const query: Record<string, string> = {}

    const locationValue = filters.l
    const effectivePath = targetPath ?? route.path
    const shouldExcludeLocation = isCottageDetailRoute(effectivePath)

    if (locationValue && !shouldExcludeLocation) {
      query.l = locationValue
    }
    if (filters.f) query.f = filters.f
    if (filters.minPrice !== undefined)
      query.min_price = String(filters.minPrice)
    if (filters.maxPrice !== undefined)
      query.max_price = String(filters.maxPrice)
    if (filters.dateIn && filters.dateOut) {
      query.d = `${filters.dateIn},${filters.dateOut}`
    }
    if (filters.c !== undefined) query.c = String(filters.c)
    if (filters.a) query.a = filters.a

    if (filters.cor) query.cor = filters.cor
    if (filters.ec !== undefined) query.ec = String(filters.ec)
    if (filters.r !== undefined) query.r = filters.r
    if (filters.bo !== undefined) query.bo = String(filters.bo)
    if (filters.of !== undefined) query.of = String(filters.of)
    if (filters.sort) query.sort = filters.sort
    if (filters.q) query.q = filters.q
    if (filters.ne) query.ne = filters.ne
    if (filters.sw) query.sw = filters.sw
    if (filters.fc !== undefined) query.fc = String(filters.fc)

    const pageNumber = page || currentPage || 1
    if (pageNumber > 1) query.page = String(pageNumber)

    // Preserve viz parameter if it exists
    if (route.query.viz === 'map') {
      query.viz = 'map'
    }

    return query
  }

  /**
   * Navigate with filters applied.
   * Defaults to /resultados so that any search or filter change lands on the
   * canonical results page. Pass an explicit `path` to stay on the current
   * page (e.g. pagination).
   */
  function navigateWithFilters(
    filters: CottageListFilters,
    page?: number,
    currentPage?: number,
    path?: string,
  ) {
    const targetPath = path ?? routes().cottageList()
    const query = buildUrlQuery(filters, page, currentPage, targetPath)

    navigateTo(
      {
        path: targetPath,
        query: Object.keys(query).length > 0 ? query : {},
      },
      { external: true },
    )
  }

  /**
   * Get current filters from URL
   */
  function getCurrentFilters(): CottageListFilters {
    const facetParams = getFacetParams()
    const params = facetParams || parseUrlParams()
    const dates = params.d ? params.d.split(',') : []

    const routeSlug = route.params.slug as string | undefined
    const isCottageListWithSlug =
      route.path.startsWith('/casas-rurales/') && routeSlug
    const locationValue =
      params.l || (isCottageListWithSlug ? routeSlug : undefined)

    const routeCorner = route.params.corner as string | undefined

    return {
      l: locationValue,
      f: params.f,
      minPrice:
        params.min_price !== undefined ? params.min_price / 100 : undefined,
      maxPrice:
        params.max_price !== undefined ? params.max_price / 100 : undefined,
      dateIn: dates[0] || undefined,
      dateOut: dates[1] || undefined,
      c: params.c,
      a: params.a,
      cor: params.cor || routeCorner,
      ec: params.ec,
      r: params.r,
      bo: params.bo,
      of: params.of,
      sort: params.sort,
      q: params.q,
      ne: params.ne,
      sw: params.sw,
      fc: params.fc,
    }
  }

  /**
   * Handle location change
   */
  function handleLocationChange(
    location: string,
    cottagesData?: CottageListData,
  ) {
    const currentFilters = getCurrentFilters()
    const mergedFilters = {
      ...currentFilters,
      l: location,
      cor: undefined,
    }

    if (shouldActivatePopunder.value) {
      const query = buildUrlQuery(mergedFilters, 1)
      const targetUrl = `${route.path}?${new URLSearchParams(query).toString()}`

      // Pass current page's cottagesData to use for HomeToGo comparison
      // The targetUrl (new tab) will navigate to the new location
      navigateWithPopunder(targetUrl, cottagesData)
      return
    }

    navigateWithFilters(mergedFilters, 1)
  }

  /**
   * Handle online booking filter toggle
   */
  function handleOnlineBookingFilter() {
    const currentFilters = getCurrentFilters()
    navigateWithFilters(
      {
        ...currentFilters,
        bo: 1,
      },
      1,
    )
  }

  /**
   * Handle exact capacity filter toggle
   */
  function handleExactCapacityFilter() {
    const currentFilters = getCurrentFilters()
    navigateWithFilters(
      {
        ...currentFilters,
        ec: 1,
      },
      1,
    )
  }

  /**
   * Remove a specific filter by ID
   */
  function handleRemoveFilter(filterId: number) {
    const currentFilters = getCurrentFilters()
    const filtersArray = currentFilters.f ? currentFilters.f.split(',') : []

    const updatedFilters = filtersArray.filter(f => f !== String(filterId))

    navigateWithFilters(
      {
        ...currentFilters,
        f: updatedFilters.length > 0 ? updatedFilters.join(',') : undefined,
      },
      1,
    )
  }

  /**
   * Update filters and navigate
   * Preserves all URL parameters that are not provided in the filters object
   */
  function updateFilters(
    filters: CottageListFilters,
    page?: number,
    cottagesData?: CottageListData,
  ) {
    const currentUrlParams = getCurrentFilters()
    const mergedFilters = { ...currentUrlParams, ...filters }

    if (shouldActivatePopunder.value) {
      const query = buildUrlQuery(mergedFilters, page)
      const targetUrl = `${route.path}?${new URLSearchParams(query).toString()}`

      // Pass cottagesData directly - extraction will be handled by hometogo module
      navigateWithPopunder(targetUrl, cottagesData)
      return
    }

    navigateWithFilters(mergedFilters, page)
  }

  function hasFiltersApplied(): boolean {
    const currentFilters = getCurrentFilters()
    return (
      !!currentFilters.f ||
      !!currentFilters.minPrice ||
      !!currentFilters.maxPrice ||
      !!currentFilters.dateIn ||
      !!currentFilters.dateOut ||
      !!currentFilters.c ||
      !!currentFilters.a ||
      !!currentFilters.ec ||
      !!currentFilters.r ||
      !!currentFilters.bo ||
      !!currentFilters.of ||
      !!currentFilters.sort ||
      !!currentFilters.q
    )
  }

  function handlePageFilter(page: number, cottagesData?: CottageListData) {
    const currentFilters = getCurrentFilters()

    if (shouldActivatePopunder.value) {
      const query = buildUrlQuery(currentFilters, page)
      const targetUrl = `${route.path}?${new URLSearchParams(query).toString()}`

      // Pass cottagesData directly - extraction will be handled by hometogo module
      navigateWithPopunder(targetUrl, cottagesData)
      return
    }

    navigateWithFilters(currentFilters, page, undefined, route.path)
  }

  return {
    parseUrlParams,
    buildUrlQuery,
    navigateWithFilters,
    getCurrentFilters,
    handleLocationChange,
    handleOnlineBookingFilter,
    handleExactCapacityFilter,
    handleRemoveFilter,
    updateFilters,
    hasFiltersApplied,
    handlePageFilter,
  }
}
